import axios from "axios";
import { WatermarkDto } from "definitions/exporter";

import type { GalleryItem } from "./components";
import {
  WATERMARK_ACCEPT_TYPES,
  WATERMARK_MAX_PER_USER,
  WATERMARK_MAX_SIZE_BYTES,
} from "./WatermarkOptionsForm.constants";

export const toGalleryItem = (dto: WatermarkDto): GalleryItem => ({
  id: dto.id,
  url: dto.url,
  filename: dto.filename,
  size: dto.size,
  mimeType: dto.mimeType,
  uploadedAt: dto.uploadedAt,
});

export const isValidHttpUrl = (value?: string): boolean => {
  if (!value) return false;
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

// RFC 2483: text/uri-list — CRLF-разделённый список, строки с # — комментарии.
// Браузеры/файл-менеджеры часто кладут URL с trailing CRLF, поэтому raw-парсинг
// через new URL(value) ломается на валидном payload'е.
export const parseDroppedUrl = (raw: string): string | null => {
  const line = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .find((l) => l.length > 0 && !l.startsWith("#"));
  return line ?? null;
};

export enum ValidationReason {
  Mime = "mime",
  Size = "size",
}

export interface ValidationResult {
  valid: boolean;
  reason?: ValidationReason;
}

export const validateWatermarkFile = (file: File): ValidationResult => {
  if (!WATERMARK_ACCEPT_TYPES.includes(file.type)) {
    return { valid: false, reason: ValidationReason.Mime };
  }
  if (file.size > WATERMARK_MAX_SIZE_BYTES) {
    return { valid: false, reason: ValidationReason.Size };
  }
  return { valid: true };
};

const getAxiosStatus = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

export const isAxiosErrorWithStatus = (
  error: unknown,
  statuses: number[],
): boolean => {
  const status = getAxiosStatus(error);
  return status !== undefined && statuses.includes(status);
};

export const mapUploadError = (error: unknown): string => {
  switch (getAxiosStatus(error)) {
    case 400:
      return "Файл повреждён или не совпадает с расширением";
    case 409:
      return `Достигнут лимит ${WATERMARK_MAX_PER_USER} картинок`;
    case 413:
      return "Файл больше 5 МБ";
    case 415:
      return "Допустимы PNG/JPEG/WebP";
    default:
      return "Не удалось загрузить файл";
  }
};
